import { type UseQueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';

import { useReduxState } from '@/app/shared/context/ReduxStoreContext';
import { useHideWhitePinsFeatureToggle } from '@/app/shared/hooks/useHideWhitePinsFeatureToggle';

import { getGeoValue } from '@/packages/JsonQuery';
import { useQueryStateAppContext } from '@/packages/query-state/shared/context/appContext';
import { type GetRealClustersResponse } from '@/packages/query-state/shared/types/clusters/ClustersResponse';
import { completeClustersJsonQuerySelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/smart';
import { mapConfigurationZoomSelector } from '@/packages/redux-state/store/slices/mapConfiguration/selectors/dumb';
import { mapConfigurationBboxSelector } from '@/packages/redux-state/store/slices/mapConfiguration/selectors/smart';
import { getCancellationTokenFromSignal } from '@/packages/utils/getCancellationTokenFromSignal';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchGetClustersService } from './internal/fetchService';

type UseGetClustersQueryOptions<TData> = UseQueryOptions<GetRealClustersResponse, QueryError, TData, QueryKey>;

export function useGetClustersQuery<TData = GetRealClustersResponse>(
  options?: UseGetClustersQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const applicationContext = useQueryStateAppContext();
  const reduxState = useReduxState();
  const hideWhitePins = useHideWhitePinsFeatureToggle();

  return useQuery<GetRealClustersResponse, QueryError, TData, QueryKey>({
    queryKey: getQueryKey(),
    queryFn: ({ signal }) => {
      const state = reduxState.getState();

      const jsonQuery = completeClustersJsonQuerySelector(state);
      delete jsonQuery.bbox;

      const hasGeoFilter = Boolean(getGeoValue()(jsonQuery));

      const bbox = mapConfigurationBboxSelector(state);
      const zoom = mapConfigurationZoomSelector(state);
      /**
       * @description
       * Параметр selectedBbox в ручке get-clusters-for-map делает все пины в этой зоне синими.
       * По сути в текущем bbox должны быть только синие пины.
       */
      const selectedBbox = hideWhitePins && !hasGeoFilter ? bbox : undefined;

      return fetchGetClustersService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters: { bbox, jsonQuery, selectedBbox, zoom },
      });
    },
    enabled: false,
    ...options,
  });
}
